import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * "Invite your pilot friends" — server side.
 *
 * A pilot pastes emails, we mail each one a link back to the event. Crew can
 * do the same, plus add a known pilot straight onto the Going or Waitlist.
 * No pilot record is created by an invite; that happens when the friend
 * confirms their own email.
 */

async function adminDb() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function myPilotId(context: { supabase: any; userId: string }): Promise<string | null> {
  const { data } = await context.supabase.rpc("pilot_id_for_user", { _user_id: context.userId });
  return (data as string | null) ?? null;
}

async function assertCrew(context: { supabase: any; userId: string }) {
  const { resolveViewerTier } = await import("@/lib/pilot-profile.functions");
  const tier = await resolveViewerTier(context as never);
  if (tier !== "crew" && tier !== "admin") throw new Error("Unauthorized");
  return tier;
}

const EVENT_COLS =
  "id, tenant_id, slug, title, starts_at, location, status, capacity, spectator_capacity";

type InviteEvent = {
  id: string;
  tenant_id: string;
  slug: string;
  title: string;
  starts_at: string;
  location: string | null;
  status: string;
  capacity: number;
};

async function loadEventBySlug(slug: string): Promise<InviteEvent> {
  const db = await adminDb();
  const { data } = await db.from("events").select(EVENT_COLS).eq("slug", slug).maybeSingle();
  if (!data) throw new Error("Could not find that event.");
  return data as unknown as InviteEvent;
}

export async function loadEventById(id: string): Promise<InviteEvent> {
  const db = await adminDb();
  const { data } = await db.from("events").select(EVENT_COLS).eq("id", id).maybeSingle();
  if (!data) throw new Error("Could not find that event.");
  return data as unknown as InviteEvent;
}

export type InviteSendResult = {
  ok: true;
  sent: string[];
  /** Already invited to this event by someone — we do not double-mail. */
  duplicates: string[];
  /** Already RSVPed, so nothing to invite. */
  alreadyGoing: string[];
  invalid: string[];
  overflow: string[];
};

export async function inviteEmails(input: {
  event: InviteEvent;
  raw: string;
  inviterPilotId: string | null;
  inviterName: string | null;
  invitedByUserId: string | null;
  source: "pilot" | "crew";
}): Promise<InviteSendResult> {
  const db = await adminDb();
  const invites = await import("@/lib/invites.server");
  const parsed = invites.parseEmailList(input.raw);

  const sent: string[] = [];
  const duplicates: string[] = [];
  const alreadyGoing: string[] = [];

  const expiresAt = new Date(
    Math.min(
      new Date(input.event.starts_at).getTime() + 24 * 3600_000,
      Date.now() + invites.INVITE_TTL_DAYS * 24 * 3600_000,
    ),
  ).toISOString();

  for (const email of parsed.emails) {
    // Someone already coming does not need an invite.
    const { data: pilotId } = await db.rpc("find_pilot_by_contact", {
      _email: email,
      _phone: null,
    } as never);
    if (pilotId) {
      const { data: rsvp } = await db
        .from("event_rsvps")
        .select("id, status")
        .eq("event_id", input.event.id)
        .eq("pilot_id", pilotId as string)
        .maybeSingle();
      if (rsvp && (rsvp as { status: string }).status !== "cancelled") {
        alreadyGoing.push(email);
        continue;
      }
    }

    const { data: existing } = await db
      .from("event_invites")
      .select("id, status")
      .eq("event_id", input.event.id)
      .eq("email_normalized", email)
      .maybeSingle();
    if (existing) {
      duplicates.push(email);
      continue;
    }

    const token = invites.newInviteToken();
    const { error } = await db.from("event_invites").insert({
      tenant_id: input.event.tenant_id,
      event_id: input.event.id,
      inviter_pilot_id: input.inviterPilotId,
      invited_by_user_id: input.invitedByUserId,
      source: input.source,
      email,
      email_normalized: email,
      token_hash: await invites.sha256Hex(token),
      status: "sent",
      sent_at: new Date().toISOString(),
      expires_at: expiresAt,
    } as never);
    if (error) {
      console.error("invite insert failed", error);
      continue;
    }

    await invites.sendInviteEmail({
      to: email,
      inviterName: input.inviterName,
      eventTitle: input.event.title,
      startsAt: input.event.starts_at,
      location: input.event.location,
      inviteUrl: invites.inviteUrlFor(input.event.slug, token),
      fromCrew: input.source === "crew",
    });
    sent.push(email);
  }

  // Sending pays a little — accepting pays more.
  if (input.inviterPilotId && sent.length) {
    const { emitEconomyEvent } = await import("@/lib/economy.server");
    for (let i = 0; i < sent.length; i++) {
      await emitEconomyEvent({
        kind: "invite_sent",
        pilotId: input.inviterPilotId,
        eventId: input.event.id,
      });
    }
  }

  return {
    ok: true as const,
    sent,
    duplicates,
    alreadyGoing,
    invalid: parsed.invalid,
    overflow: parsed.overflow,
  };
}

/** A signed-in pilot invites friends to an event. */
export const sendEventInvites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(120), emails: z.string().max(4000) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as { supabase: any; userId: string };
    const pilotId = await myPilotId(ctx);
    if (!pilotId) throw new Error("Finish your pilot profile before inviting friends.");

    const event = await loadEventBySlug(data.slug);
    if (event.status === "cancelled") throw new Error("That event was cancelled.");

    const db = await adminDb();
    const { data: me } = await db
      .from("pilots")
      .select("first_name, last_name, callsign")
      .eq("id", pilotId)
      .maybeSingle();
    const p = me as {
      first_name: string | null;
      last_name: string | null;
      callsign: string | null;
    } | null;

    return inviteEmails({
      event,
      raw: data.emails,
      inviterPilotId: pilotId,
      inviterName: p?.callsign || `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.trim() || null,
      invitedByUserId: ctx.userId,
      source: "pilot",
    });
  });

/** How an invited friend sees the event page before they RSVP. */
export const resolveInvite = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().trim().min(20).max(200) }).parse(data),
  )
  .handler(async ({ data }) => {
    const invites = await import("@/lib/invites.server");
    const invite = await invites.findInviteByToken(data.token);
    if (!invite) return { valid: false as const };

    const db = await adminDb();
    let inviterName: string | null = null;
    if (invite.inviter_pilot_id) {
      const { data: row } = await db
        .from("pilots")
        .select("first_name, callsign")
        .eq("id", invite.inviter_pilot_id)
        .maybeSingle();
      const p = row as { first_name: string | null; callsign: string | null } | null;
      inviterName = p?.callsign || p?.first_name || null;
    }

    if (invite.status === "sent") {
      await invites.markInvite(invite.id, {
        status: "opened",
        opened_at: new Date().toISOString(),
      });
    }

    return {
      valid: true as const,
      email: invite.email,
      inviterName,
      fromCrew: invite.source === "crew",
    };
  });

/* ------------------------------------------------------------------ *
 * Crew: add attendees by hand
 * ------------------------------------------------------------------ */

/** Type-ahead over pilots for the marshal's "add attendee" dialog. */
export const searchPilotsForEvent = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ eventId: z.string().uuid(), q: z.string().trim().max(80) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertCrew(context as never);
    if (data.q.length < 2) return { pilots: [] as any[] };

    const db = await adminDb();
    const term = data.q.replace(/[%,]/g, " ").trim();
    const numeric = Number.parseInt(term, 10);
    const filters = [
      `first_name.ilike.%${term}%`,
      `last_name.ilike.%${term}%`,
      `callsign.ilike.%${term}%`,
      `email.ilike.%${term}%`,
    ];
    if (Number.isFinite(numeric)) filters.push(`pilot_no.eq.${numeric}`);

    const { data: rows } = await db
      .from("pilots")
      .select("id, first_name, last_name, callsign, pilot_no, email")
      .is("archived_at", null)
      .or(filters.join(","))
      .limit(8);

    return { pilots: (rows ?? []) as any[] };
  });

export type CrewAddResult = {
  ok: true;
  outcome: "going" | "waitlisted" | "invited";
  message: string;
};

/**
 * Marshal adds someone to an event. A known pilot goes straight on; an unknown
 * email gets the same invite a friend would send. Over the pilot limit the
 * addition lands on the waitlist unless the marshal opened another heat.
 */
export const crewAddAttendee = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        pilotId: z.string().uuid().optional(),
        emails: z.string().max(4000).optional(),
        list: z.enum(["going", "waitlist"]).default("going"),
        guestCount: z.number().int().min(0).max(6).default(0),
        role: z.enum(["pilot", "spectator"]).default("pilot"),
        payment: z.enum(["unpaid", "credited"]).default("unpaid"),
        compNote: z.string().trim().max(120).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }): Promise<CrewAddResult> => {
    const ctx = context as { supabase: any; userId: string };
    await assertCrew(ctx);

    const event = await loadEventById(data.eventId);
    const db = await adminDb();

    // Unknown people: invite flow, no pilot record yet.
    if (!data.pilotId) {
      if (!data.emails?.trim()) throw new Error("Pick a pilot or enter an email.");
      const result = await inviteEmails({
        event,
        raw: data.emails,
        inviterPilotId: null,
        inviterName: null,
        invitedByUserId: ctx.userId,
        source: "crew",
      });
      const parts: string[] = [];
      if (result.sent.length) parts.push(`Invited ${result.sent.length}`);
      if (result.duplicates.length) parts.push(`${result.duplicates.length} already invited`);
      if (result.alreadyGoing.length)
        parts.push(`${result.alreadyGoing.length} already on the list`);
      if (result.invalid.length) parts.push(`${result.invalid.length} unreadable`);
      return { ok: true, outcome: "invited", message: parts.join(" · ") || "Nothing to send." };
    }

    // Known pilot: capacity decides Going vs Waitlist.
    const spectator = data.role === "spectator";
    const { data: goingRows } = await db
      .from("event_rsvps")
      .select("guest_count, attend_role, pilot_id")
      .eq("event_id", event.id)
      .eq("status", "going");
    const going = (
      (goingRows ?? []) as {
        guest_count: number;
        attend_role: string | null;
        pilot_id: string | null;
      }[]
    ).filter((r) => r.pilot_id !== data.pilotId);
    const spectatorsNow =
      going.filter((r) => r.attend_role === "spectator").length +
      going.reduce((s, r) => s + (r.guest_count ?? 0), 0);
    const pilotsNow = going.filter((r) => r.attend_role !== "spectator").length;
    const { decideRsvpPlacement } = await import("@/lib/rsvp-placement");
    const placement = decideRsvpPlacement({
      going: pilotsNow,
      guests: spectatorsNow,
      capacity: event.capacity ?? Number.MAX_SAFE_INTEGER,
      spectatorCapacity: (event as { spectator_capacity?: number | null }).spectator_capacity ?? 0,
      requestedGuests: spectator ? 0 : data.guestCount,
      role: data.role,
    });
    const status = data.list === "waitlist" ? "waitlisted" : placement.status;

    const { data: prior } = await db
      .from("event_rsvps")
      .select("id")
      .eq("event_id", event.id)
      .eq("pilot_id", data.pilotId)
      .maybeSingle();

    const row = {
      tenant_id: event.tenant_id,
      event_id: event.id,
      pilot_id: data.pilotId,
      status,
      guest_count: spectator ? 0 : placement.guestCount,
      source: "manual",
      attend_role: data.role,
      freq_status: spectator ? "not_required" : "pending",
      responded_at: new Date().toISOString(),
    };

    let rsvpId: string | null = prior ? (prior as { id: string }).id : null;
    if (rsvpId) {
      await db
        .from("event_rsvps")
        .update(row as never)
        .eq("id", rsvpId);
    } else {
      const { data: inserted, error } = await db
        .from("event_rsvps")
        .insert(row as never)
        .select("id")
        .single();
      if (error || !inserted) {
        console.error("crew add attendee failed", error);
        throw new Error("Could not add that pilot.");
      }
      rsvpId = (inserted as { id: string }).id;
    }

    // Comped seat: settle through the same path as Mark paid → comp, so it is
    // audited, reminders stop, and the pilot is seated. Never counts as money.
    let comped = false;
    if (data.payment === "credited" && !spectator && rsvpId) {
      const { applyRsvpPayment } = await import("@/lib/rsvp-payment.server");
      await applyRsvpPayment(db, {
        rsvpId,
        state: "credited",
        note: data.compNote ? `Comped: ${data.compNote}` : "Comped seat",
        actorUserId: ctx.userId,
      });
      comped = true;
    }

    // Same instant confirmation a self-serve RSVP gets.
    try {
      const { data: fullEvent } = await db
        .from("events")
        .select(
          "id, title, slug, description, location, starts_at, ends_at, price_cents, kit_price_cents, kit_label, payment_link_url, payment_link_with_kit_url",
        )
        .eq("id", event.id)
        .maybeSingle();
      if (fullEvent && rsvpId) {
        const comms = await import("@/lib/event-comms.server");
        await comms.sendRsvpNotice({
          db,
          rsvpId,
          pilotId: data.pilotId,
          event: fullEvent as never,
          seatLine: null,
          wantsKit: false,
          status,
          spectator,
          resendTag: `crew-${status}-${data.role}`,
        });
      }
    } catch (e) {
      console.error("crew add confirmation email failed", e);
    }

    return {
      ok: true,
      outcome: status === "going" ? "going" : "waitlisted",
      message:
        status === "going"
          ? spectator
            ? "Added as a spectator. Confirmation emailed."
            : comped
              ? "Added as a comped seat. Confirmation emailed."
              : "Added to Going. Confirmation emailed."
          : spectator
            ? "Spectator spots are full — added to the waitlist."
            : "Over the pilot limit — added to the waitlist. Open another heat to seat them.",
    };
  });

/** Marshal opens another heat so more pilots can be seated. */
export const addHeatForEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ eventId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertCrew(context as never);
    const event = await loadEventById(data.eventId);
    const db = await adminDb();
    const seating = await import("@/lib/rsvp-seating.server");
    const session = await seating.ensureSessionForEventServer(event as never);

    const { data: heats } = await db
      .from("session_heats")
      .select("heat_no")
      .eq("session_id", session.id);
    const nextNo =
      Math.max(0, ...((heats ?? []) as { heat_no: number }[]).map((h) => h.heat_no)) + 1;

    const { error } = await db.from("session_heats").insert({
      tenant_id: event.tenant_id,
      session_id: session.id,
      heat_no: nextNo,
      status: "planned",
    } as never);
    if (error) throw new Error("Could not open another heat.");

    return { ok: true as const, heatNo: nextNo };
  });
